import { UniqueId } from "shared/vidio_helpers";
import PlayerEnvironment from "shared/player_environment";
import AhoyEvent from "shared/ahoy_event";
import { BugsnagAdapter } from "commons/BugsnagAdapter";

/**
 * @description playback/base.js still use this
 */
window.PlayerEnvironment = PlayerEnvironment;
export default class PlaybackBaseLoader {
  static init(baseUrl, baseCanaryUrl) {
    this._baseUrl = baseUrl;
    this._baseCanaryUrl = baseCanaryUrl;
    this._playbackId = UniqueId.get();
  }

  static getBaseUrl() {
    return this._baseUrl;
  }

  static getBaseCanaryUrl() {
    return this._baseCanaryUrl;
  }

  static getPlaybackId() {
    return this._playbackId;
  }

  static isCanary() {
    return this.params().match(/playback-canary=true/) !== null;
  }

  static params() {
    return window.location.search;
  }

  static setup() {
    if (this.isCanary()) {
      this.appendScript(this.getBaseCanaryUrl());
    } else {
      this.appendScript(this.getBaseUrl());
    }
  }

  static appendScript(scriptUrl) {
    return this.append(scriptUrl)
      .then(
        function () {
          this.sendAhoyEvent("LOADED");
        }.bind(this)
      )
      .catch(
        function () {
          this.sendAhoyEvent("FAILED");
        }.bind(this)
      );
  }

  static sendAhoyEvent(name) {
    var eventName = "PLAYBACK::BASE::" + name;
    AhoyEvent.send(eventName, this.properties());
  }

  static properties() {
    var property = {
      playback_uuid: PlaybackBaseLoader.getPlaybackId(),
      path: PlaybackBaseLoader.getPath(),
      revision: PlaybackBaseLoader.getRevisionTag(),
    };
    return Object.assign(property, PlaybackBaseLoader.env());
  }

  static env() {
    return PlayerEnvironment.currentEnv();
  }

  static getRevisionTag() {
    if (
      typeof window.playback !== "undefined" &&
      typeof window.playback.Revision !== "undefined"
    ) {
      return window.playback.Revision.tag;
    }
  }

  static getPath() {
    return window.location.pathname;
  }

  static append(src) {
    return new Promise(function (resolve, reject) {
      try {
        var script = document.createElement("script");
        var body = document.getElementsByTagName("body")[0];

        script.onload = resolve;
        script.src = src;
        body.appendChild(script);

        resolve();
      } catch (e) {
        reject();
      }
    });
  }

  static configure({
    baseUrl,
    canaryBaseUrl,
    imaUrl,
    enableCustomSkipAdButtonToggle,
    enableRefactoredBannerAdToggle,
    enablePremierUserBannerAdsToggle,
    enableShowAdsByPackageToggle,
    enableEmbedHideComponentToggle,
    enableVp9WebToken,
    enableApiForAdsParamsToggle,
    enabledPersistentEmbedTopBar,
    enableOpenwrapPubmaticToggle,
    enableNextVideoServiceToggle,
    loadImaJsOnHead,
    cppWatchpageEnabled,
    dvrEnabled,
    loadBaseJsInHead
  } = {}) {
    window.playback_feature_toggle = [];
    this.checkEnableCustomSkipAdButton(enableCustomSkipAdButtonToggle);
    this.checkEnableRefactoredBannerAd(enableRefactoredBannerAdToggle);
    this.checkEnablePremierUserBannerAds(enablePremierUserBannerAdsToggle);
    this.checkEnableShowAdsByPackage(enableShowAdsByPackageToggle);
    this.checkEnableEmbedHideComponent(enableEmbedHideComponentToggle);
    this.checkEnableVp9WebToken(enableVp9WebToken);
    this.checkEnableApiForAdsParams(enableApiForAdsParamsToggle);
    this.checkEnabledPersistentEmbedTopBar(enabledPersistentEmbedTopBar);
    this.checkEnableOpenwrapPubmatic(enableOpenwrapPubmaticToggle);
    this.checkEnableNextVideoService(enableNextVideoServiceToggle);
    this.checkCppWatchpageEnabled(cppWatchpageEnabled);
    this.checkDvrEnabled(dvrEnabled);

    if (loadImaJsOnHead === 'true') {
      return this.initThenSetup(baseUrl, canaryBaseUrl, loadBaseJsInHead);
    } else {
      return this.appendImaThenSetup({ imaUrl, baseUrl, canaryBaseUrl, loadBaseJsInHead });
    }
  }

  static appendImaThenSetup({ imaUrl, baseUrl, canaryBaseUrl, loadBaseJsInHead } = {}) {
    return this.append(imaUrl).then(() => {
      this.initThenSetup(baseUrl, canaryBaseUrl, loadBaseJsInHead);
    }).catch((e) => {
      this.initThenSetup(baseUrl, canaryBaseUrl, loadBaseJsInHead);
      BugsnagAdapter.notify("ImaUrlFailedLoad", e);
    });
  }

  static checkEnableCustomSkipAdButton(enableCustomSkipAdButtonToggle) {
    if (enableCustomSkipAdButtonToggle == "true") {
      window.playback_feature_toggle.push(
        "enable_custom_skip_ad_button_on_player"
      );
    }
  }

  static checkEnableRefactoredBannerAd(enableRefactoredBannerAdToggle) {
    if (enableRefactoredBannerAdToggle == "true") {
      window.playback_feature_toggle.push("enable_refactored_banner_ad");
    }
  }

  static checkEnablePremierUserBannerAds(enablePremierUserBannerAdsToggle) {
    if (enablePremierUserBannerAdsToggle == "true") {
      window.playback_feature_toggle.push("enable_premier_user_banner_ads");
    }
  }

  static checkEnableShowAdsByPackage(enableShowAdsByPackageToggle) {
    if (enableShowAdsByPackageToggle == "true") {
      window.playback_feature_toggle.push("enable_show_ads_by_package");
    }
  }

  static checkEnableEmbedHideComponent(toggle) {
    if (toggle == 'true') {
      window.playback_feature_toggle.push('enable_embed_hide_component');
    }
  }

  static checkEnableVp9WebToken(toggle) {
    if (toggle == 'true') {
      window.playback_feature_toggle.push('vp9_web_token');
    }
  }

  static checkEnableApiForAdsParams(enableApiForAdsParamsToggle) {
    if (enableApiForAdsParamsToggle == "true") {
      window.playback_feature_toggle.push("enable_api_for_ads_params");
    }
  }

  static checkEnabledPersistentEmbedTopBar(toggle) {
    if (toggle == 'true') {
      window.playback_feature_toggle.push('persistent_embed_top_bar');
    }
  }

  static checkEnableOpenwrapPubmatic(enableOpenwrapPubmaticToggle) {
    if (enableOpenwrapPubmaticToggle == 'true') {
      window.playback_feature_toggle.push('enable_openwrap_pubmatic');
    }
  }

  static checkEnableNextVideoService(enableNextVideoServiceToggle) {
    if (enableNextVideoServiceToggle == 'true') {
      window.playback_feature_toggle.push('enable_next_video_service');
    }
  }

  static checkCppWatchpageEnabled(toggle) {
    if (toggle == 'true') {
      window.playback_feature_toggle.push('cpp_watchpage_enabled');
    }
  }

  static checkDvrEnabled(toggle) {
    if (toggle == 'true') {
      window.playback_feature_toggle.push('dvr_enabled');
    }
  }

  static initThenSetup(baseUrl, canaryBaseUrl, loadBaseJsInHead) {
    window.playbackProperties = this.properties;
    PlaybackBaseLoader.init(baseUrl, canaryBaseUrl);

    if (loadBaseJsInHead === 'true') {
      PlaybackBaseLoader.sendPlaybackBaseAhoyEvent();
    } else {
      PlaybackBaseLoader.setup();
    }
  }

  static sendPlaybackBaseAhoyEvent() {
    const status = window.playback === undefined ? 'FAILED' : 'LOADED';
    this.sendAhoyEvent(status);
  }
}
